import { Injectable } from '@angular/core';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import {
	ComponentController,
	filterNil,
	filterNull,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, PrimeNGConfig } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, combineLatest, fromEvent, of } from 'rxjs';
import {
	debounceTime,
	map,
	shareReplay,
	startWith,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';

import { CoacheeMoodDialogComponent } from 'dialog/coachee-mood-dialog/coachee-mood-dialog.component';
import { MoodDescriptionDialogComponent } from 'dialog/mood-description-dialog/mood-description-dialog.component';
import { environment } from 'environments/environment';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

import { AppState, AppStore } from './app.store';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AppController extends ComponentController<AppState> {
	public i18n = 'presentation';

	public showChat$: Observable<boolean>;
	public appChatUser$: Observable<IUser>;
	public chatStatus$: Observable<string>;
	public isLoading$: Observable<boolean>;
	public isConnected$: Observable<boolean>;
	public sideBarStatus$: Observable<string>;
	public isRedirecting$: Observable<boolean>;
	public appChatWidthPx$: Observable<number>;
	public showFocusMode$: Observable<boolean>;
	public appSlideWidthPx$: Observable<number>;
	public showAppToolbar$: Observable<boolean>;
	public appPaddingTopPx$: Observable<number>;
	public appToolbarHeight$: Observable<number>;
	public contentMarginLeftPx$: Observable<number>;
	public showChatToggleButton$: Observable<boolean>;
	public showSidebarToggleButton$: Observable<boolean>;
	public showDisableFocusMode$: Observable<boolean>;
	public languageOptions$: Observable<MenuItem[]>;
	public language$: Observable<string>;

	constructor(
		store: AppStore,
		private _config: PrimeNGConfig,
		private _dialogService: DialogService,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public disableFocusMode() {
		this.store.update({ focusModeEnabled: false });
	}

	public openMoodDialog() {
		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				filterNil(),
				take(1),
				tap((user) =>
					this._dialogService.open(
						user.metadata.moodDescriptionShown
							? CoacheeMoodDialogComponent
							: MoodDescriptionDialogComponent,
						{
							header: this._translateService.instant(
								user.metadata.moodDescriptionShown
									? this.i18n + '.coacheeMoodDialog.header'
									: this.i18n + '.moodDescriptionDialog.header'
							),
							dismissableMask: true,
							width: '500px',
						}
					)
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initListeners() {
		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				debounceTime(500),
				map((user) => user?.metadata?.language),
				filterNull(),
				tap((language) => this._translateService.use(language)),
				tap((language) => this._translateService.setDefaultLang(language)),
				tap(() =>
					this._config.setTranslation(
						this._translateService.instant('presentation.primeng.calendar')
					)
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		const windowWidth$ = fromEvent(window, 'resize').pipe(
			map((_) => window.innerWidth),
			startWith(window.innerWidth),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.language$ = this._userApplicationPort.observeSessionUser().pipe(
			map((user) => user?.metadata?.language || 'en'),
			startWith(this._translateService.currentLang)
		);

		this.languageOptions$ = this._translateService
			.stream('presentation.app.languages')
			.pipe(
				map((languages) =>
					environment.availableLocales
						.filter((locale) => !!languages[locale.lang])
						.map(
							(locale) =>
								({
									label: languages[locale.lang],
									icon: `flag-icon flag-icon-${locale.flag}`,
									command: () =>
										this._translateService
											.use(locale.lang)
											.pipe(
												switchMap(() => this._userApplicationPort.observeSessionUser()),
												switchMap((sessionUser) =>
													sessionUser
														? this._userApplicationPort.updateUserLanguage(locale.lang)
														: of(null)
												),
												take(1)
											)
											.subscribe(),
									styleClass:
										this._translateService.currentLang === locale.lang
											? 'p-background-color-primary-pastel'
											: '',
								} as MenuItem)
						)
				),
				shareReplay({ bufferSize: 1, refCount: true })
			);

		this.showDisableFocusMode$ = combineLatest([
			this.select('focusModeEnabled'),
			this.select('showFocusMode'),
		]).pipe(
			map(
				([focusModeEnabled, showFocusMode]) => focusModeEnabled && showFocusMode
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		const isSidebarOpened$ = combineLatest([
			this.select('focusModeEnabled'),
			this.select('sideBarOpened'),
			windowWidth$,
		]).pipe(
			map(([focusModeEnabled, sideBarOpened, windowWidth]) => ({
				sideBarOpened,
				focusModeEnabled,
				windowWidth,
			})),
			map(
				(data) =>
					(data.sideBarOpened || data.windowWidth >= 1150) && !data.focusModeEnabled
			)
		);

		const isChatOpened$ = combineLatest([
			this.select('focusModeEnabled'),
			this.select('chatOpened'),
		]).pipe(
			map(([focusModeEnabled, chatOpened]) => ({
				focusModeEnabled,
				chatOpened,
			})),
			map((data) => data.chatOpened && !data.focusModeEnabled)
		);

		this.isConnected$ = this._userApplicationPort.observeSessionUser().pipe(
			map((user) => !!user && user.isCoachee),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.showFocusMode$ = combineLatest([
			this.isConnected$,
			this.select('showFocusMode'),
		]).pipe(
			map(([isConnected, showFocusMode]) => isConnected && showFocusMode),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appChatUser$ = this.select('appChatUserId').pipe(
			switchMap((userId) =>
				userId
					? this._userApplicationPort
							.observeUserById(userId)
							.pipe(map((data) => data.data))
					: this._userApplicationPort
							.observeSessionUser()
							.pipe(switchMap((user) => (user ? user.coach$ : of(null))))
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.contentMarginLeftPx$ = combineLatest([
			this.select('focusModeEnabled'),
			this.select('appSlideWidthPx'),
			this.isConnected$,
			windowWidth$,
		]).pipe(
			map(([focusModeEnabled, appSlideWidthPx, isConnected, windowWidth]) => ({
				focusModeEnabled,
				appSlideWidthPx,
				isConnected,
				windowWidth,
			})),
			map((data) =>
				data.windowWidth >= 1150 && data.isConnected && !data.focusModeEnabled
					? data.appSlideWidthPx
					: 0
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.showAppToolbar$ = combineLatest([
			this.isConnected$,
			this.select('focusModeEnabled'),
		]).pipe(
			map(([isConnected, focusModeEnabled]) => isConnected && !focusModeEnabled),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appPaddingTopPx$ = combineLatest([
			this.showAppToolbar$,
			this.select('appToolbarHeight'),
		]).pipe(
			map(([showAppToolbar, appToolbarHeight]) => ({
				showAppToolbar,
				appToolbarHeight,
			})),
			map((data) => (data.showAppToolbar ? data.appToolbarHeight : 0)),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.showSidebarToggleButton$ = combineLatest([
			this.select('focusModeEnabled'),
			this.contentMarginLeftPx$,
		]).pipe(
			map(([focusModeEnabled, contentLeftMargin]) => ({
				focusModeEnabled,
				contentLeftMargin,
			})),
			map((data) => !data.focusModeEnabled && data.contentLeftMargin === 0),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.showChatToggleButton$ = this.select('focusModeEnabled').pipe(
			map((focusModeEnabled) => !focusModeEnabled),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.showChat$ = combineLatest([this.isConnected$, this.appChatUser$]).pipe(
			map(([isConnected, appChatUser]) => isConnected && !!appChatUser),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.sideBarStatus$ = isSidebarOpened$.pipe(
			map((isOpened) => (isOpened ? 'opened' : 'closed')),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.chatStatus$ = isChatOpened$.pipe(
			map((isOpened) => (isOpened ? 'opened' : 'closed')),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.appSlideWidthPx$ = this.select('appSlideWidthPx').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appChatWidthPx$ = this.select('appChatWidthPx').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appToolbarHeight$ = this.select('appToolbarHeight').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.isRedirecting$ = this.select('redirecting').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.isLoading$ = this.selectLoading().pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}
}
